import React, { useState, useEffect } from 'react';
import { scheduleAPI, pageAPI } from '../api/endpoints';
import './ScheduleBuilder.css';

interface PageFile {
  filename: string;
  originalName: string;
  mimeType: string;
  path: string;
  order: number;
}

interface Page {
  _id: string;
  title: string;
  description: string;
  files: PageFile[];
  duration: number;
  transition: string;
}

interface ScheduleSlot {
  pageId: Page | string;
  order: number;
  duration?: number;
}

interface Schedule {
  _id: string;
  name: string;
  description: string;
  slots: ScheduleSlot[];
  transition: string;
  autoplay: boolean;
  loop: boolean;
  createdAt: string;
}

export const ScheduleBuilder: React.FC = () => {
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [pages, setPages] = useState<Page[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);

  // Create form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [transition, setTransition] = useState('slide');
  const [autoplay, setAutoplay] = useState(true);
  const [loop, setLoop] = useState(true);
  const [slots, setSlots] = useState<{ pageId: string; duration: number }[]>([]);

  // Edit state
  const [editId, setEditId] = useState<string | null>(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [schedRes, pageRes] = await Promise.all([
        scheduleAPI.getAll(),
        pageAPI.getAll(),
      ]);
      setSchedules(schedRes.data.data);
      setPages(pageRes.data.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setName('');
    setDescription('');
    setTransition('slide');
    setAutoplay(true);
    setLoop(true);
    setSlots([]);
    setEditId(null);
    setShowCreate(false);
  };

  const handleAddSlot = () => {
    if (pages.length === 0) return;
    setSlots([...slots, { pageId: pages[0]._id, duration: 10 }]);
  };

  const handleRemoveSlot = (index: number) => {
    setSlots(slots.filter((_, i) => i !== index));
  };

  const handleSlotChange = (index: number, field: 'pageId' | 'duration', value: string | number) => {
    const updated = [...slots];
    (updated[index] as any)[field] = value;
    setSlots(updated);
  };

  const moveSlot = (index: number, direction: -1 | 1) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= slots.length) return;
    const updated = [...slots];
    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
    setSlots(updated);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name || slots.length === 0) {
      alert('Name und mindestens ein Slot sind erforderlich');
      return;
    }

    const data = {
      name,
      description,
      transition,
      autoplay,
      loop,
      slots: slots.map((s, i) => ({
        pageId: s.pageId,
        order: i,
        duration: s.duration,
      })),
    };

    try {
      if (editId) {
        await scheduleAPI.update(editId, data);
      } else {
        await scheduleAPI.create(data);
      }
      resetForm();
      fetchData();
    } catch (error: any) {
      alert('Fehler: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleEdit = (schedule: Schedule) => {
    setEditId(schedule._id);
    setName(schedule.name);
    setDescription(schedule.description || '');
    setTransition(schedule.transition);
    setAutoplay(schedule.autoplay);
    setLoop(schedule.loop);
    setSlots(
      schedule.slots
        .sort((a, b) => a.order - b.order)
        .map((slot) => ({
          pageId: typeof slot.pageId === 'string' ? slot.pageId : slot.pageId._id,
          duration: slot.duration || (typeof slot.pageId === 'object' ? slot.pageId.duration : 10),
        }))
    );
    setShowCreate(true);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Schedule wirklich löschen?')) return;
    try {
      await scheduleAPI.delete(id);
      fetchData();
    } catch (error) {
      alert('Fehler beim Löschen');
    }
  };

  const getPageTitle = (pageId: string | Page | null): string => {
    if (!pageId) return '(gelöscht)';
    if (typeof pageId === 'object') return pageId.title;
    const page = pages.find((p) => p._id === pageId);
    return page?.title || 'Unbekannt';
  };

  return (
    <div className="schedule-builder">
      <div className="schedule-header">
        <h2>Schedules</h2>
        <button className="btn-primary" onClick={() => {
          if (showCreate && !editId) { resetForm(); } else { resetForm(); setShowCreate(true); }
        }}>
          {showCreate ? 'Abbrechen' : '+ Neuer Schedule'}
        </button>
      </div>

      {showCreate && (
        <form className="schedule-form" onSubmit={handleSubmit}>
          <h3>{editId ? 'Schedule bearbeiten' : 'Neuer Schedule'}</h3>

          <div className="form-row">
            <div className="form-group">
              <label>Name *</label>
              <input type="text" value={name} onChange={(e) => setName(e.target.value)} required placeholder="z.B. Hauptprogramm" />
            </div>
            <div className="form-group">
              <label>Übergang</label>
              <select value={transition} onChange={(e) => setTransition(e.target.value)}>
                <option value="slide">Slide</option>
                <option value="fade">Fade</option>
                <option value="convex">Convex</option>
                <option value="concave">Concave</option>
                <option value="zoom">Zoom</option>
                <option value="none">Ohne</option>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label>Beschreibung</label>
            <input type="text" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Optional" />
          </div>

          <div className="form-row-checks">
            <label><input type="checkbox" checked={autoplay} onChange={(e) => setAutoplay(e.target.checked)} /> Automatisch abspielen</label>
            <label><input type="checkbox" checked={loop} onChange={(e) => setLoop(e.target.checked)} /> Endlosschleife</label>
          </div>

          <div className="slots-section">
            <div className="slots-header">
              <h4>Seiten-Reihenfolge</h4>
              <button type="button" className="btn-small btn-secondary" onClick={handleAddSlot} disabled={pages.length === 0}>
                + Seite hinzufügen
              </button>
            </div>

            {pages.length === 0 && (
              <p className="hint">Erstelle zuerst Seiten unter "Seiten".</p>
            )}

            {slots.map((slot, index) => (
              <div key={index} className="slot-row">
                <span className="slot-order">{index + 1}.</span>
                <select value={slot.pageId} onChange={(e) => handleSlotChange(index, 'pageId', e.target.value)}>
                  {pages.map((page) => (
                    <option key={page._id} value={page._id}>{page.title} ({page.files.length} Dateien)</option>
                  ))}
                </select>
                <input
                  type="number"
                  value={slot.duration}
                  onChange={(e) => handleSlotChange(index, 'duration', parseInt(e.target.value) || 10)}
                  min={1}
                  className="duration-input"
                  title="Dauer in Sekunden"
                />
                <span className="duration-label">Sek.</span>
                <div className="slot-actions">
                  <button type="button" className="btn-tiny" onClick={() => moveSlot(index, -1)} disabled={index === 0}>▲</button>
                  <button type="button" className="btn-tiny" onClick={() => moveSlot(index, 1)} disabled={index === slots.length - 1}>▼</button>
                  <button type="button" className="btn-tiny btn-danger" onClick={() => handleRemoveSlot(index)}>✕</button>
                </div>
              </div>
            ))}
          </div>

          <button type="submit" className="btn-primary" disabled={!name || slots.length === 0}>
            {editId ? 'Speichern' : 'Schedule erstellen'}
          </button>
        </form>
      )}

      {loading ? (
        <p>Laden...</p>
      ) : schedules.length === 0 ? (
        <p className="empty-state">Noch keine Schedules erstellt.</p>
      ) : (
        <div className="schedules-list">
          {schedules.map((schedule) => (
            <div key={schedule._id} className="schedule-card">
              <div className="schedule-card-header">
                <div>
                  <h3>{schedule.name}</h3>
                  {schedule.description && <p className="schedule-desc">{schedule.description}</p>}
                  <div className="schedule-meta">
                    <span>{schedule.slots.length} Seite(n)</span>
                    <span>Übergang: {schedule.transition}</span>
                    {schedule.autoplay && <span>Autoplay</span>}
                    {schedule.loop && <span>Loop</span>}
                  </div>
                </div>
                <div className="schedule-actions">
                  <button className="btn-small btn-secondary" onClick={() => handleEdit(schedule)}>Bearbeiten</button>
                  <button className="btn-small btn-danger" onClick={() => handleDelete(schedule._id)}>Löschen</button>
                </div>
              </div>

              <div className="schedule-slots-preview">
                {schedule.slots
                  .sort((a, b) => a.order - b.order)
                  .map((slot, i) => (
                    <div key={i} className="slot-preview">
                      <span className="slot-num">{i + 1}</span>
                      <span>{getPageTitle(slot.pageId)}</span>
                      <span className="slot-dur">{slot.duration || (slot.pageId && typeof slot.pageId === 'object' ? slot.pageId.duration : '?')}s</span>
                    </div>
                  ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
